import {
  Card,
  Container,
  Autocomplete,
  Box,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
  debounce,
  Tooltip,
  TextField,
} from '@mui/material';
import CaseRow from './CaseRow';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import Page from 'src/components/Page';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import { WorkflowInstanceManager } from 'src/@types/crs/referral';
import { LoadingTableRow, TableHeadCustom } from 'src/components/table';
import { PaginateQuery, PaginateQueryWithStats } from 'src/api/pagination/dtos';
import { CaseService, PaginatedCaseDto } from 'src/crs/case/service/CaseService';
import FilterDrawer from 'src/components/FilterDrawer';
import { wait } from 'src/utils/timers';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useNotiMessage from 'src/hooks/useNotiMessage';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { sortBy } from 'lodash';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';
import useTable from 'src/hooks/useTable';
import { workflowInstanceMap } from 'src/careflow/common/common-utils';
import { TableToolbar } from 'src/careflow/common';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { translateTableHeadCustom } from 'src/careflow/helpers/common';
import { TABLE_HEAD_CASES_LIST } from 'src/careflow/common/table-head';
import NoResultsTableRow from 'src/careflow/common/NoResultsTableRow';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';

interface CaseListProps {
  includeBreadcrumbs;
  tableData: PaginatedCaseDto | undefined | null;
  setTableData: Dispatch<SetStateAction<PaginatedCaseDto | undefined | null>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  assignedTo: any;
  setAssignedTo: Dispatch<SetStateAction<any>>;
  setStats?: Dispatch<SetStateAction<any>>;
  caseStatus: string;
  setCaseStatus: Dispatch<SetStateAction<string>>;
  show?: boolean;
  showFilters?: boolean;
}

export default function CaseList({
  includeBreadcrumbs,
  tableData,
  setTableData,
  search,
  setSearch,
  isLoading,
  setIsLoading,
  assignedTo,
  setAssignedTo,
  setStats,
  caseStatus,
  setCaseStatus,
  show = true,
  showFilters = true,
}: CaseListProps) {
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage, setPage } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });

  const { themeStretch } = useSettings();
  const [isOpenAplyFilters, setIsOpenAplyFilters] = useState<boolean>(false);

  const [allUsers, setAllUsers] = useState<any[]>([]);
  const { i18n } = useLocales();
  const { showErrorMessage } = useNotiMessage();

  const authUser = useAuth().getCurrentUser();

  const userOrganizations = useMemo(() => authUser?.organizations ?? [], [authUser?.organizations]);

  const orgsKey = useMemo(() => JSON.stringify(userOrganizations), [userOrganizations]);

  const getPaginateQuery = (
    page: number,
    rowsPerPage: number,
    search: string,
    assignedTo: any,
    caseStatus: string
  ) => {
    const paginateQuery: PaginateQueryWithStats = { page: page, limit: rowsPerPage };
    if (search) {
      paginateQuery.search = search;
    }
    paginateQuery.filter = {};

    if (assignedTo?.length) {
      paginateQuery.filter.workflowOwnedBy = `$in:${assignedTo?.join(',')}`;
    }

    if (caseStatus) {
      paginateQuery.filter.workflowStatus = `$eq:${caseStatus}`;
    }

    return paginateQuery;
  };

  const fetchCaseList = async (
    page: number,
    rowsPerPage: number,
    search: string,
    assignedTo: any,
    caseStatus: string
  ) => {
    setIsLoading(true);
    await wait();

    const cases = await CaseService.getAll(
      getPaginateQuery(page, rowsPerPage, search, assignedTo, caseStatus)
    );
    setIsLoading(false);
    if (!cases) return showErrorMessage(i18n('error.case.list'));

    setTableData(cases);
    if (setStats && cases?.stats) {
      setStats(cases?.stats);
    }
  };

  const getCaseList = useCallback(debounce(fetchCaseList, 600), []);

  useEffect(() => {
    getCaseList(page, rowsPerPage, search, assignedTo, caseStatus);
    getAllUsers();
  }, [page, rowsPerPage, search, assignedTo, caseStatus, orgsKey]);

  const handlePageChange = (event: unknown, newPage: number) => {
    onChangePage(event, newPage + 1);
  };
  const getAllUsers = async () => {
    const result = await UserService.getUsersByOrgAndRole(userOrganizations, []);
    setAllUsers(
      sortBy(
        result.filter((e: any) => e.status === 'Active'),
        ['userName']
      )
    );
  };
  const handleSearch = (query: string) => {
    setSearch(query);
    setPage(1);
  };

  const dataFormatted = (): WorkflowInstanceManager[] => {
    const data = tableData?.data
      ? tableData.data.map((r) => workflowInstanceMap(r, PATH_DASHBOARD.crs.case))
      : [];
    return data;
  };

  const rows = dataFormatted();

  if (!show) {
    return null;
  }

  return (
    <Page title={i18n('case.list.title', 'crs')}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        {includeBreadcrumbs ? (
          <HeaderBreadcrumbs
            title={i18n('case.list.title', 'crs')}
            heading=""
            links={[
              { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
              { name: `${i18n('breadcrumbs.title', 'crs')}` },
              { name: `${i18n('case.list.title', 'crs')}`, href: PATH_DASHBOARD.crs.case },
            ]}
          />
        ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Stack direction="row" sx={{ py: 2, px: 2 }}>
                <Grid xs={6}>
                  <TableToolbar filterMrn={search} onFilterMrn={handleSearch} />
                </Grid>
                <Grid container alignItems="center" justifyContent="flex-end" xs={6}>
                  <Tooltip title="Filter">
                    {showFilters ? (
                      assignedTo?.length > 0 ? (
                        <IconButton
                          onClick={() => {
                            setIsOpenAplyFilters(true);
                          }}
                          sx={{ height: '40px', backgroundColor: '#7ac25b' }}
                        >
                          <FilterListIcon htmlColor="#087736" />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => {
                            setIsOpenAplyFilters(true);
                          }}
                          sx={{ height: '40px' }}
                        >
                          <FilterListIcon htmlColor="#8f9993" />
                        </IconButton>
                      )
                    ) : (
                      <></>
                    )}
                  </Tooltip>
                </Grid>
              </Stack>
              <TableContainer>
                <Table>
                  <TableHeadCustom
                    headLabel={translateTableHeadCustom(i18n, TABLE_HEAD_CASES_LIST, 'crs')}
                  />
                  <TableBody>
                    {!isLoading ? (
                      rows?.length ? (
                        rows.map((row) => <CaseRow key={row.id} row={row} />)
                      ) : (
                        <NoResultsTableRow
                          colSpan={TABLE_HEAD_CASES_LIST.length}
                          text={i18n('case.list.listNotFound', 'crs')}
                        />
                      )
                    ) : (
                      <LoadingTableRow colSpan={TABLE_HEAD_CASES_LIST.length} />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid xs={12}>
                <Stack justifyContent="flex-end">
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    count={tableData ? tableData.meta.totalItems : 0}
                    rowsPerPage={rowsPerPage}
                    page={page - 1}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={onChangeRowsPerPage}
                    sx={{ borderTop: 0 }}
                  />
                </Stack>
              </Grid>
            </Card>
          </Grid>
          <FilterDrawer
            onApplyButtonClick={() => {
              getCaseList(page, rowsPerPage, search, assignedTo, caseStatus);
              setIsOpenAplyFilters(false);
            }}
            title={i18n('case.list.filterPopUp.title', 'crs')}
            onCloseIconButtonClick={() => {
              setIsOpenAplyFilters(false);
            }}
            onClearAllButtonClick={() => {
              setAssignedTo(null);
            }}
            anchor={'right'}
            open={isOpenAplyFilters}
          >
            <TaskFilterList
              setAssignedTo={setAssignedTo}
              assignedTo={assignedTo}
              setAllUsers={setAllUsers}
              users={allUsers}
              i18n={i18n}
            />
          </FilterDrawer>
        </Grid>
      </Container>
    </Page>
  );
}
interface TaskFilterListProps {
  setAssignedTo: any;
  assignedTo: any;
  setAllUsers: any;
  users: any;
  i18n?: any;
}
const TaskFilterList = ({
  setAssignedTo,
  assignedTo,
  setAllUsers,
  users,
  i18n,
}: TaskFilterListProps) => (
  <Box py={3}>
    <Box>
      <Grid item sx={{ marginTop: 1 }} xs={6}>
        <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
          {i18n('case.list.filterPopUp.assignedTo', 'crs')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Autocomplete
            value={assignedTo ?? []}
            multiple
            fullWidth
            onChange={(_: React.SyntheticEvent, taskStatus) => {
              setAssignedTo([...taskStatus]);
            }}
            options={users?.map((option: any) => option?.email)}
            getOptionLabel={(option: string) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n('case.list.filterPopUp.assignedTo', 'crs')}
                variant="outlined"
              />
            )}
          />
        </Stack>
      </Grid>
    </Box>
  </Box>
);
